import { DEFAULT_LOCALE, isSupportedLocale } from '../i18n/locales';
import { setPrefetched } from '../static-prefetch';
import { supabase } from '../supabase';
import { tourDetailColumns, type Tour } from '../tours';

// Shared by the native and web variants of the app's app/[lang]/tours/[id]. Fetched once
// per export process (not once per locale) — generateStaticParams is called
// for every cascaded {lang}, and tours don't vary by locale.
let toursPromise: Promise<Tour[]> | null = null;

function fetchAllTours(): Promise<Tour[]> {
  if (!toursPromise) {
    toursPromise = Promise.resolve(supabase.from('tours').select(tourDetailColumns())).then(
      ({ data, error }) => {
        if (error) throw new Error(`generateStaticParams(tours): ${error.message}`);
        // A column list built at runtime: postgrest-js can't type the rows.
        return (data ?? []) as unknown as Tour[];
      },
    );
  }
  return toursPromise;
}

// Argument shape: expo-router passes `{ params: parentParams }`, not the
// params directly — see the note in the app's article-detail.ts for what getting this
// wrong cost there. Tours don't vary by locale, so only the
// returned `lang` matters here.
export async function generateTourStaticParams({
  params,
}: {
  params: { lang?: string };
}): Promise<{ lang: string; id: string }[]> {
  const tours = await fetchAllTours();
  for (const tour of tours) setPrefetched('tour', tour.id, tour);
  // Fall back the same way the app's article-detail.ts does, so a missing parent param
  // can never silently produce a route with an undefined locale segment.
  const lang = isSupportedLocale(params.lang) ? params.lang : DEFAULT_LOCALE;
  return tours.map((tour) => ({ lang, id: tour.id }));
}
